"use client";

import { ArrowRight, Building2, Megaphone, Code, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IndustryTrust() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const integrations = [{
    name: "Bandaranaike Academy for Leadership & Public Policy (BALPP)",
    role: "Technical Assistant",
    description: "Worked for more than one year as a Technical Assistant, contributing as part of the Information Technology team at the Bandaraike Academy for Leadership & Public Policy (BALPP), an institution operating under the Bandaraike Memorial Foundation at the BMICH premises.",
    logos: ["https://yasindhuravith.com/wp-content/uploads/2026/06/Logo-5YV_IVr1-2.png?v=3mct81kjg1w", "https://yasindhuravith.com/wp-content/uploads/2026/06/Logo-Trns-1.png?v=jedk2v0b4ds"],
    icon: Building2
  }, {
    name: "Penta Technology Solutions",
    role: "Digital Marketing",
    description: "Joined Penta Technology Solutions as a Digital Marketer and helped strengthen the company's digital presence across multiple platforms. Managed social media marketing, branding initiatives, website content, campaign strategy, graphic design, corporate communications, and marketing materials.",
    logos: ["https://yasindhuravith.com/wp-content/uploads/2026/06/cropped-cropped-Penta-technology-solutions-logo-2.png?v=xsndxof4jje"],
    icon: Megaphone
  }, {
    name: "YOTTRIO",
    role: "Web Design & Digital Growth Specialist",
    description: "Currently working as the Web Design & Digital Growth Specialist at YOTTRIO. Helping transform the company's online presence through modern website design, user experience improvements, technical SEO implementation, performance optimisation, content structure, and long-term digital growth strategies.",
    logos: ["https://yasindhuravith.com/wp-content/uploads/2026/07/YOTTRIO-Sri-Lankas-Best-Education-Consultancy-Service-2026.png?v=qb45an3dpp"],
    icon: Code
  }, {
    name: "CIPS Sri Lanka",
    role: "Digital Launch\xA0",
    description: "Led the complete digital marketing strategy, content planning, campaign execution, and promotional activities for introducing CIPS UK qualifications to Sri Lanka through GCAS. Developed the launch strategy, created all marketing assets, and generated qualified student leads.",
    logos: ["https://yasindhuravith.com/wp-content/uploads/2026/07/logo-design-latest-1024x511-1.png?v=j1bwnmhgryn", "https://yasindhuravith.com/wp-content/uploads/2026/07/CIPS_Centre-Excellence_Logo_2022.png?v=s6im0lkquiq"],
    icon: TrendingUp
  }];
  return <section data-section-id="industry-trust" id="industry-trust" className="relative overflow-hidden py-24 text-foreground bg-card-foreground">
      {/* Ambient background glow */}
      <div className="pointer-events-none absolute -top-24 left-1/2 h-[500px] w-[900px] -translate-x-1/2 rounded-3xl bg-gradient-to-r from-purple-400/15 via-indigo-300/15 to-blue-400/15 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 left-10 h-80 w-80 rounded-3xl bg-primary/10 blur-[100px]" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-10 right-10 h-80 w-80 rounded-3xl bg-blue-400/10 blur-[100px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <div ref={headerRef} className="mx-auto max-w-[800px] text-center transition-all duration-800 ease-out" style={{
        transform: headerVisible ? "translateY(0)" : "translateY(40px)",
        opacity: headerVisible ? 1 : 0,
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          <span className="font-mono text-[0.75rem] font-medium uppercase tracking-[0.2em] text-primary">
            Brands I've Worked With
          </span>
          <h2 className="mt-4 font-default text-[clamp(2rem,5vw,3.5rem)] font-bold leading-[1.2] tracking-tight text-primary">
            Trusted by{" "}
            <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
              Leading Organizations
            </span>
          </h2>
          <p className="mt-6 text-muted-foreground">
            Organizations and brands where I have contributed through digital marketing, web development, strategy, branding, technical operations, and digital transformation.
          </p>
        </div>

        <div ref={gridRef} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {integrations.map((item, i) => <div data-index={i} key={item.name} className="group relative flex flex-col rounded-2xl border border-slate-100 bg-white p-8 shadow-xl shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-purple-500/10" style={{
          transform: gridVisible ? "translateY(0)" : "translateY(40px)",
          opacity: gridVisible ? 1 : 0,
          transitionDelay: gridVisible ? `${i * 100}ms` : "0ms",
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
              <div className="mb-6 flex min-h-[48px] items-center justify-center gap-3">
                {item.logos.map((logo, idx) => <img key={idx} src={logo} alt={item.name} className="h-12 w-auto object-contain" loading="lazy" />)}
              </div>

              <h3 className="text-center font-default text-lg font-bold leading-snug text-purple-700">
                {item.name}
              </h3>

              <p className="mt-2 text-center font-default text-sm font-semibold text-indigo-600">
                {item.role}
              </p>

              <div className="mx-auto my-3 w-3/4 border-b border-slate-100" />

              <p className="flex-grow text-left font-default text-[0.85rem] leading-relaxed text-muted-foreground">
                {item.description}
              </p>

              <div className="mt-8 flex flex-col items-center">
                <Button asChild className="h-11 w-full rounded-xl bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-lg shadow-red-500/30 transition-all hover:brightness-110">
                  <Link to="/PortfolioPage">
                    Learn More <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}